import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { type RecordSourceSelectorProxy, ConnectionHandler } from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import type { useWorkSuggestionsAction_WorkSuggestionsRemoveTaskMutation as WorkSuggestionsRemoveTaskMutation } from '@atlassian/jira-relay/src/__generated__/useWorkSuggestionsAction_WorkSuggestionsRemoveTaskMutation.graphql';
import type { useWorkSuggestionsAction_WorkSuggestionsSnoozeTaskMutation as WorkSuggestionsSnoozeTaskMutation } from '@atlassian/jira-relay/src/__generated__/useWorkSuggestionsAction_WorkSuggestionsSnoozeTaskMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import RelayDataID from '@atlassian/relay-data-id';
import { getUuidFromTask, isTaskTypeInId } from '../../common/utils';
import { useWorkSuggestionsContainer } from '../periscope-container';

export const useWorkSuggestionsAction = () => {
	const [{ selectedProjectAris, sprintAutoDiscoveryLimit }] = useWorkSuggestionsContainer();

	const cloudId = useCloudId();
	const [commitSnoozeMutation] = useMutation<WorkSuggestionsSnoozeTaskMutation>(graphql`
		mutation useWorkSuggestionsAction_WorkSuggestionsSnoozeTaskMutation(
			$input: WorkSuggestionsActionInput!
		) {
			workSuggestions @optIn(to: "WorkSuggestionsMutation") {
				snoozeTask(input: $input) {
					success
				}
			}
		}
	`);

	const [commitRemoveMutation] = useMutation<WorkSuggestionsRemoveTaskMutation>(graphql`
		mutation useWorkSuggestionsAction_WorkSuggestionsRemoveTaskMutation(
			$input: WorkSuggestionsActionInput!
		) {
			workSuggestions @optIn(to: "WorkSuggestionsMutation") {
				removeTask(input: $input) {
					success
				}
			}
		}
	`);

	const removeTaskFromStore = useCallback(
		(
			store: RecordSourceSelectorProxy,
			{
				id,
				typename,
			}: {
				id: string;
				typename: string;
			},
		) => {
			// To remove task in the relay store
			const workSuggestionsRecord = store
				.get('client:root:workSuggestions')
				?.getLinkedRecord('suggestionsByProjects', {
					cloudId,
					projectAris: selectedProjectAris,
					sprintAutoDiscoveryLimit,
				});

			if (workSuggestionsRecord == null) return;

			const connections = ConnectionHandler.getConnection(
				workSuggestionsRecord,
				'periscope__commonSuggestions',
			);
			const autoDevJobsRecords = workSuggestionsRecord.getLinkedRecords('autoDevJobsSuggestions');
			const removeTaskDataId = RelayDataID({ id }, typename);

			if (autoDevJobsRecords) {
				workSuggestionsRecord.setLinkedRecords(
					autoDevJobsRecords.filter((record) => record.getDataID() !== removeTaskDataId),
					'autoDevJobsSuggestions',
				);
			}

			const prSuggestionsRecords = workSuggestionsRecord.getLinkedRecords('pullRequestSuggestions');
			if (prSuggestionsRecords) {
				workSuggestionsRecord.setLinkedRecords(
					prSuggestionsRecords.filter((record) => record.getDataID() !== removeTaskDataId),
					'pullRequestSuggestions',
				);
			}

			const compassSuggestionsRecords =
				workSuggestionsRecord.getLinkedRecords('compassSuggestions');
			if (compassSuggestionsRecords) {
				workSuggestionsRecord.setLinkedRecords(
					compassSuggestionsRecords.filter((record) => record.getDataID() !== removeTaskDataId),
					'compassSuggestions',
				);
			}

			if (!connections || !removeTaskDataId) return;

			ConnectionHandler.deleteNode(connections, removeTaskDataId);
		},
		[cloudId, selectedProjectAris, sprintAutoDiscoveryLimit],
	);

	const snooze = useCallback(
		({ typename, id }: { typename: string; id: string }) => {
			if (!cloudId) return;

			const uuid =
				isTaskTypeInId(id, typename) && fg('jira-suggestion-id-sync-with-ers-enabled')
					? id
					: getUuidFromTask({ typename, id });
			commitSnoozeMutation({
				variables: {
					input: { cloudId, taskId: uuid },
				},
				optimisticUpdater: (store) => {
					removeTaskFromStore(store, { typename, id });
				},
				updater: (store, data) => {
					if (data?.workSuggestions?.snoozeTask?.success) {
						removeTaskFromStore(store, { typename, id });
					}
					// Error handling
				},
				onError() {
					// TODO: adding a flag to show the error message
					// showFlag({
					//   type: 'error',
					//   title: 'errorTitleMessage',
					//   description: "We couldn't complete your request. Please try again.",
					// });
				},
			});
		},
		[commitSnoozeMutation, removeTaskFromStore, cloudId],
	);

	const remove = useCallback(
		({ typename, id }: { typename: string; id: string }) => {
			const uuid = fg('jira-suggestion-id-sync-with-ers-enabled')
				? id
				: getUuidFromTask({ typename, id });

			if (!cloudId) return;
			commitRemoveMutation({
				variables: {
					input: { cloudId, taskId: uuid },
				},
				optimisticUpdater: (store) => {
					removeTaskFromStore(store, { typename, id });
				},
				updater: (store, data) => {
					if (data?.workSuggestions?.removeTask?.success) {
						removeTaskFromStore(store, { typename, id });
					}
					// Error handling
				},
				onError() {
					// TODO: adding a flag to show the error message
					// showFlag({
					//   type: 'error',
					//   title: 'errorTitleMessage',
					//   description: "We couldn't complete your request. Please try again.",
					// });
				},
			});
		},
		[commitRemoveMutation, removeTaskFromStore, cloudId],
	);

	return { snooze, remove };
};
